// Scheduled Messages List Modal for active conversation (WP-04).
import { useState, useEffect, useCallback, useId, useRef } from 'react';
import { useI18n } from '../../lib/i18n';
import { cn } from '../../lib/cn';
import { useDialogA11y } from '../../lib/useDialogA11y';
import {
  listScheduledMessages,
  cancelScheduledMessage,
  type ScheduledMessageDoc,
} from '../../lib/scheduled';
import type { LiveConversation } from '../../lib/inboxTypes';
import { CloseIcon, ClockIcon, TrashIcon } from '../icons';
import { useToast } from '../ui/Toast';
import { useConfirm } from '../ui/ConfirmDialog';

interface ScheduledListModalProps {
  conv: LiveConversation;
  onClose: () => void;
}

export default function ScheduledListModal({ conv, onClose }: ScheduledListModalProps) {
  const { t } = useI18n();
  const toast = useToast();
  const confirm = useConfirm();
  const [items, setItems] = useState<ScheduledMessageDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useDialogA11y(true, panelRef, onClose);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listScheduledMessages({ conversationId: conv.id });
      if (res.ok && Array.isArray(res.items)) {
        setItems(res.items);
      }
    } catch (err) {
      setError((err as Error).message || 'Failed to fetch scheduled messages');
    } finally {
      setLoading(false);
    }
  }, [conv.id]);

  useEffect(() => {
    void fetchItems();
  }, [fetchItems]);

  const handleCancel = async (id: string) => {
    if (!(await confirm({
      title: t('inbox.schedule.cancel'),
      body: t('inbox.schedule.cancelConfirm'),
      confirmLabel: t('inbox.schedule.cancel'),
      cancelLabel: t('common.keep'),
      danger: true,
    }))) return;
    try {
      await cancelScheduledMessage({ id });
      setItems((prev) =>
        prev.map((it) => (it.id === id ? { ...it, status: 'cancelled' } : it)),
      );
    } catch (err) {
      toast.error((err as Error).message || t('inbox.schedule.cancelFailed'));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="flex h-[75vh] max-h-[540px] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-line bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-line px-5 py-4 dark:border-slate-800">
          <div className="flex items-center gap-2.5">
            <div className="grid h-8 w-8 place-items-center rounded-lg bg-amber-50 text-amber-600 dark:bg-amber-950/40 dark:text-amber-400">
              <ClockIcon size={18} />
            </div>
            <div>
              <h3 id={titleId} className="text-sm font-bold text-ink dark:text-slate-100">
                {t('inbox.schedule.listTitle') || 'Scheduled Messages'}
              </h3>
              <p className="text-xs text-ink-3 dark:text-slate-400">
                {conv.displayName || conv.name} ({conv.phone})
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close') || 'Close'}
            className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <CloseIcon size={16} />
          </button>
        </div>

        {/* List */}
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {error && (
            <div className="mb-3 rounded-lg bg-rose-50 p-3 text-xs font-semibold text-rose-600 dark:bg-rose-950/40 dark:text-rose-400">
              {error}
            </div>
          )}

          {loading ? (
            <div className="grid h-40 place-items-center text-xs text-ink-3 dark:text-slate-500">
              {t('common.loading') || 'Loading scheduled messages…'}
            </div>
          ) : items.length === 0 ? (
            <div className="grid h-40 place-items-center text-xs text-ink-3 dark:text-slate-500">
              {t('inbox.schedule.empty') || 'No scheduled messages for this conversation'}
            </div>
          ) : (
            <div className="space-y-2.5">
              {items.map((it) => {
                const sendAtStr = new Date(it.sendAt).toLocaleString([], {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                });
                return (
                  <div
                    key={it.id}
                    className="flex items-start justify-between gap-3 rounded-xl border border-line bg-surface p-3.5 dark:border-slate-800 dark:bg-slate-850"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span
                          className={cn(
                            'rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider',
                            it.status === 'pending'
                              ? 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400'
                              : it.status === 'sent'
                              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400'
                              : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
                          )}
                        >
                          {it.status}
                        </span>
                        <span className="text-xs font-bold text-ink dark:text-slate-200">
                          ⏱ {sendAtStr} ({it.timezone})
                        </span>
                      </div>

                      <div className="mt-1 text-xs text-ink-2 dark:text-slate-300">
                        {it.payload?.templateName ? (
                          <span className="font-semibold text-primary">
                            Template: {it.payload.templateName}
                          </span>
                        ) : (
                          it.payload?.body || '[Media message]'
                        )}
                      </div>
                    </div>

                    {it.status === 'pending' && (
                      <button
                        type="button"
                        onClick={() => void handleCancel(it.id)}
                        title={t('inbox.schedule.cancel') || 'Cancel Schedule'}
                        aria-label={t('inbox.schedule.cancel') || 'Cancel Schedule'}
                        className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-950/40 dark:hover:text-rose-400"
                      >
                        <TrashIcon size={15} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
